import m, {type Children, MithrilComponent} from '@bitstillery/mithril'
import {classes, present} from '@bitstillery/common/lib/utils'

import {Link} from '../link/link'
import {
    cached_media_folder_files,
    load_media_folder_files,
    media_folder_file_src,
    type CmsMediaFolderFile,
} from '@bitstillery/common/cms/lib/media'
import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'

/** CMS media library folder holding the no-image placeholder tiles (staff can add/remove freely). */
const PLACEHOLDER_FOLDER = 'card/placeholder'

/**
 * Round-robin cursor: each card that needs a placeholder takes the next tile in sequence, so
 * successively-mounted cards cycle through the whole set before any repeats. The picked tile is
 * stored per instance (see `oninit`) so it stays put across redraws — only a fresh mount rotates.
 */
let placeholder_cursor = 0

/** Null while the CMS folder hasn't loaded yet (or is empty) — no image tile shows until then. */
function next_placeholder(files: CmsMediaFolderFile[]): string | null {
    if (files.length === 0) {
        return null
    }
    const index = placeholder_cursor % files.length
    placeholder_cursor += 1
    return media_folder_file_src(present(files[index], 'placeholder file'))
}

export const cardMeta = {
    tier: 'content',
    summary:
        'The single card — a square image tile with a glass label (title + optional description) over it, or an `overlay` slot for content (e.g. a `<Stat/>`).',
    when_to_use:
        'Compose Cards inside a Grid. A Card is always an image tile: pass an `image` (a placeholder tile shows when it is missing) plus a `title`/`description` for the glass label, or an `overlay` to centre content over the photo. `href`/`onclick` make it navigable. Photos crop to fill the tile; set `fit="contain"` for artwork that must stay whole, such as a brand logo.',
    example: `
    <Card
        description="1,200 offers in stock"
        href="/offers"
        image="/common/img/catalog/example-placeholder.jpg"
        title="Whisky"
    />
    `,
    variants: [
        {
            prop: 'overlay',
            jsx: `
            <Card image="/common/img/catalog/example-placeholder.jpg" overlay={<Stat label="Offers in stock" suffix="+" value={3500} />} />
            `,
        },
        {
            prop: 'wash',
            label: 'wash={false}',
            jsx: `
            <Card
                image="/common/img/catalog/example-placeholder.jpg"
                overlay={<Stat label="Offers in stock" suffix="+" value={3500} />}
                wash={false}
            />
            `,
        },
        {
            prop: 'disabled',
            jsx: `
            <Card description="Sold out" disabled image="/common/img/catalog/example-placeholder.jpg" title="Whisky" />
            `,
        },
    ],
} satisfies CmsComponentMeta

export interface CardAttrs {
    /** Image URL (media library / static bundle / external). A placeholder tile shows when omitted. */
    image?: string
    /** Label heading shown over the image. */
    title?: Children
    /** Supporting line under the title, inside the label. */
    description?: Children
    /** When set, the whole card links here. */
    href?: string
    /** Click handler — renders the card as a button (e.g. auth-gated funnel navigation). Ignored if `href` is set. */
    onclick?: (event?: Event) => void
    /** Custom content centred over the image (e.g. a `<Stat/>`) — shown instead of the label. */
    overlay?: Children
    /**
     * How the image fills the tile: `cover` (default) crops it to the tile, `contain` shows the whole
     * image on a white plate — for artwork that must not be cropped, e.g. a brand logo.
     */
    fit?: 'cover' | 'contain'
    /** Teal photo-wash over an `overlay` image (default true). Set `false` to show a colour photo as-is. */
    wash?: boolean
    /** Greyed-out, non-interactive state — the card ignores `href`/`onclick` and reads as unavailable (e.g. a funnel tile with a zero count). */
    disabled?: boolean
}

/**
 * The single card component — always a square image tile with a glass label (or an `overlay` slot)
 * over the photo. Its look follows its content, so there is no variant to pick. Compose `Card`s
 * inside a `Grid`.
 */
export class Card extends MithrilComponent<CardAttrs> {
    /** Placeholder tile picked once when the card mounts, held stable across redraws. */
    placeholder_src: string | null = null

    override oninit() {
        const cached = cached_media_folder_files(PLACEHOLDER_FOLDER)
        this.placeholder_src = next_placeholder(cached)
        if (cached.length === 0) {
            void load_media_folder_files(PLACEHOLDER_FOLDER).then((files) => {
                if (files.length > 0) {
                    this.placeholder_src = next_placeholder(files)
                    m.redraw()
                }
            })
        }
    }

    view(vnode: m.Vnode<CardAttrs>) {
        const a = vnode.attrs
        const has_image = typeof a.image === 'string' && a.image.trim() !== ''
        const has_overlay = a.overlay != null && a.overlay !== false
        const is_disabled = a.disabled === true
        const alt = typeof a.title === 'string' ? a.title : ''
        const class_name = classes(
            'c-card',
            a.fit === 'contain' && 'c-card-contained',
            has_overlay && 'c-card-has-overlay',
            has_overlay && a.wash !== false && 'c-card-washed',
            is_disabled && 'c-card-disabled',
        )

        const visual = (
            <div className='c-card-visual'>
                {has_image ? (
                    <img className='c-card-image' src={a.image} alt={alt} />
                ) : this.placeholder_src ? (
                    <img className='c-card-image' src={this.placeholder_src} alt='' aria-hidden='true' />
                ) : null}
                {has_overlay ? <div className='c-card-overlay'>{a.overlay}</div> : null}
                {!has_overlay && (a.title || a.description) ? (
                    <div className='c-card-label'>
                        {a.title ? <span className='c-card-label-title'>{a.title}</span> : null}
                        {a.description ? <span className='c-card-label-description'>{a.description}</span> : null}
                    </div>
                ) : null}
            </div>
        )

        // A disabled card is inert: it drops its link/handler and reads as unavailable.
        if (!is_disabled && typeof a.href === 'string' && a.href !== '') {
            return (
                <Link className={class_name} href={a.href} type='info'>
                    {visual}
                </Link>
            )
        }
        if (!is_disabled && typeof a.onclick === 'function') {
            return (
                <button className={class_name} onclick={a.onclick} type='button'>
                    {visual}
                </button>
            )
        }
        return (
            <div className={class_name} aria-disabled={is_disabled ? 'true' : undefined}>
                {visual}
            </div>
        )
    }
}
